import {
  CheckBox,
  CheckBoxOutlineBlank,
  CheckBoxOutlined,
  DomainDisabledOutlined,
  Restore,
  Save,
} from '@mui/icons-material';
import {
  Box, Button, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import * as React from 'react';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import Grid from '@mui/material/Grid';
import { objectsEqual } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { getBuildingPath } from 'shared/routes/building';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { GridWrapper } from 'shared/components/data-display/GridFormatter';

function displayValue(valueType, dot1x) {
  if (!dot1x) return 'Nas/VTR not set';
  switch (valueType) {
    case 'vsz':
      return dot1x?.smartzone?.vsz ? dot1x.smartzone.vsz.ip : '-';
    case 'zone':
      return dot1x?.smartzone ? dot1x.smartzone.zone_name : '-';
    case 'wlan':
      return dot1x?.smartzone ? dot1x.smartzone?.wlan_ssid : '-';
    default:
      return 'N/A';
  }
}

/**
 * @param {Array} buildings - Array of buildings to display
 * @param {Object} state - Current state containing vsz, wlan, zone configurations
 * @param {Object} [state.vsz] - Virtual SmartZone configuration
 * @param {Object} [state.wlan] - WLAN configuration
 * @param {Object} [state.zone] - Zone configuration
 * @param {Function} onSave - Callback function when zone changes are saved
 */
export default function ZoneDataGrid({
  buildings, state = { vsz: {}, wlan: {}, zone: {} }, onSave,
}) {
  const { vsz, wlan, zone } = state;
  const gridData = buildings.map((building) => ({
    name: building.name,
    id: building.id,
    vsz: displayValue('vsz', building.dot1x),
    zone: displayValue('zone', building.dot1x),
    wlan: displayValue('wlan', building.dot1x),
    dot1x: building.dot1x,
  }));
  const [selectedBuildings, setSelectBuildings] = useState([]);
  const [dirty, setDirty] = useState(false);
  const theme = useTheme();
  const compactedColumns = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (selectedBuildings.length > 0) {
      setDirty(true);
    } else {
      setDirty(false);
    }
  }, [selectedBuildings.length]);

  const handleOnSave = useCallback(() => {
    onSave(selectedBuildings);
    setSelectBuildings([]);
  }, [onSave, selectedBuildings]);

  const handleRevert = useCallback(() => {
    setSelectBuildings([]);
    setDirty(false);
  }, []);

  const configIsEqual = useCallback((row) => (
    row.vsz === vsz?.ip
    && row.zone === zone?.name
    && row.wlan === wlan?.ssid
  ), [vsz, zone, wlan]);

  const renderIcon = useCallback((row) => {
    if (selectedBuildings.some((curr) => curr.id === row.id)) {
      return <CheckBox />;
    } if (configIsEqual(row)) {
      return <CheckBoxOutlined />;
    }
    if (!row.dot1x) {
      return <DomainDisabledOutlined />;
    }
    return <CheckBoxOutlineBlank />;
  }, [selectedBuildings, configIsEqual]);

  const handleCheckClick = useCallback((row) => {
    if (selectedBuildings.some((curr) => curr.id === row.id)) {
      const updatedDirtyBuildings = selectedBuildings.filter((curr) => !objectsEqual(curr, row));
      setSelectBuildings(updatedDirtyBuildings);
    } else {
      setSelectBuildings([...selectedBuildings, row]);
    }
  }, [selectedBuildings]);

  const cellLink = (params) => (
    <a href={getBuildingPath(params.row.id)}>
      {params.row.name}
    </a>
  );

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Building',
      width: compactedColumns ? 150 : 300,
      renderCell: cellLink,
    },
    {
      field: 'vsz',
      headerName: 'Current VSZ',
      type: 'string',
      width: 180,
    },
    {
      field: 'zone',
      headerName: 'Current Smartzone',
      type: 'string',
      width: 180,
    },
    {
      field: 'wlan',
      headerName: 'Current WLAN',
      type: 'string',
      width: 180,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Set Default',
      editable: true,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={renderIcon(row)}
          label="Set to Default"
          onClick={() => handleCheckClick(row)}
          color="inherit"
          disabled={!vsz || !zone || !wlan || !row.dot1x || configIsEqual(row)}
        />,
      ],
    },
  ], [vsz, zone, wlan, handleCheckClick, compactedColumns, configIsEqual, renderIcon]);

  return (
    <Box>
      <Box sx={{
        width: '100%',
      }}
      >
        <GridWrapper size={12} title="Selected Defaults">
          <Grid
            container
            size={{ xs: 12 }}
            columnSpacing={2}
            sx={{
              justifyContent: 'space-between',
              marginBottom: 1,
            }}
          >
            <Grid size={{ xs: 12, md: 4 }}>
              <DataFormatter
                descriptions={[{
                  label: 'VSZ',
                  key: 'ip',
                  readOnly: true,
                }]}
                fallback="No VSZ was selected for configuration"
                data={vsz || null}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <DataFormatter
                descriptions={[{
                  label: 'Smartzone',
                  key: 'name',
                  readOnly: true,
                }]}
                fallback="No Smartzone was selected for configuration"
                data={zone || (vsz ? null : undefined)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <DataFormatter
                descriptions={[{
                  label: 'WLAN',
                  key: 'ssid',
                  readOnly: true,
                }]}
                fallback="No WLAN was selected for configuration"
                data={wlan || (zone ? null : undefined)}
              />
            </Grid>
          </Grid>
        </GridWrapper>
        <GridWrapper size={12} title="Configure Buildings">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              mb: 2,
            }}
          >
            <Typography color="text.secondary">
              Total Results&nbsp;
              {buildings.length}
            </Typography>
          </Box>
          {buildings.length === 0 ? (
            <Typography>This complex has no buildings</Typography>
          ) : (
            <DataGrid
              rows={gridData}
              columns={columns}
              experimentalFeatures={{ clipboardPaste: true }}
              hideFooter
            />
          )}
          <Box
            style={{ marginBottom: 8 }}
            sx={{
              display: 'flex',
              justifyContent: 'end',
              gap: 1,
              py: 2,
            }}
          >
            {dirty && (
              <Button
                onClick={() => handleRevert()}
                startIcon={<Restore />}
                variant={VARIANTS.outlined}
                color={BUTTON_COLORS.error}
              >
                Reset
              </Button>
            )}
            <Button
              onClick={() => handleOnSave()}
              startIcon={<Save />}
              variant={VARIANTS.contained}
              disabled={!dirty || !vsz || !zone || !wlan}
            >
              Save
            </Button>
          </Box>
        </GridWrapper>
      </Box>
    </Box>
  );
}
